import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { BehaviorSubject, map, Observable } from 'rxjs';
import {
  DialogField
} from '@shared/components/platform-tool/platform-tool-dialog-definition/models/dialog-defnition-models';
import { ICustomValidatorModel, eliteFormGroupValidator, ValidatorHelper } from '@shared/components/platform-tool/platform-tool-dialog-definition/models/validators-model';
import {
  DialogFieldFormatterHelper
} from "@shared/components/platform-tool/platform-tool-dialog-definition/models/dialog-field-formatters";
import {takeUntil} from "rxjs/operators";

interface ValidationState {
  groupErrors: { [key: string]: number };
  totalErrors: number;
}

@Injectable({
  providedIn: 'root'
})
export class EliteFormValidationService {
  private formGroups: { [key: string]: FormGroup } = {};
  private validationState = new BehaviorSubject<ValidationState>({
    groupErrors: {},
    totalErrors: 0
  });

  constructor() { }

  registerFormGroup(groupId: string, formGroup: FormGroup, unSubscribe$: any): void {
    this.formGroups[groupId] = formGroup;
    this.updateValidationState();

    // Subscribe to form value changes
    formGroup.valueChanges.pipe(takeUntil(unSubscribe$)).subscribe(() => {
      this.updateValidationState();
    });
  }

  unregisterFormGroup(groupId: string): void {
    delete this.formGroups[groupId];
    this.updateValidationState();
  }

  getValidationState(): Observable<ValidationState> {
    return this.validationState.asObservable();
  }


  triggerValidation(controlKey: string, groupId?: string): void {
    const control = this.getControl(controlKey, groupId);
    if (control) {
      control.updateValueAndValidity();
      control.markAsTouched();
    }
  }

  getErrorMessage(controlKey: string, validatorKey: string, groupId?: string, placeholders?: any): string {
    const formGroup = groupId ? this.formGroups[groupId] : this.findFormGroupByControl(controlKey);
    if (!formGroup) return '';

    return ValidatorHelper.getErrorMessage(
      formGroup,
      validatorKey,
      controlKey,
      placeholders
    );
  }

  isValid(controlKey: string, groupId?: string): boolean {
    const control = this.getControl(controlKey, groupId);
    return control ? control.valid : false;
  }

  getErrors(controlKey: string, groupId?: string): ValidationErrors | null {
    const control = this.getControl(controlKey, groupId);
    return control ? control.errors : null;
  }

  resetControl(controlKey: string, groupId?: string): void {
    const control = this.getControl(controlKey, groupId);
    if (control) {
      control.reset();
      control.markAsUntouched();
    }
  }

  setValue(controlKey: string, value: any, groupId?: string, options: { emitEvent?: boolean; onlySelf?: boolean } = {}): void {
    const control = this.getControl(controlKey, groupId);
    if (control) {
      control.setValue(value, options);
    }
  }

  getControlValidationState(controlKey: string, groupId?: string): Observable<boolean> {
    const control = this.getControl(controlKey, groupId);

    if (!control) return new Observable<boolean>();

    return control.statusChanges.pipe(
      map(() => control.valid)
    );
  }

  private getControl(controlKey: string, groupId?: string): FormControl | null {
    const formGroup = groupId ?
      this.formGroups[groupId] :
      this.findFormGroupByControl(controlKey);

    return formGroup?.get(controlKey) as FormControl;
  }

  private findFormGroupByControl(controlKey: string): FormGroup | null {
    return Object.values(this.formGroups).find(
      group => group.contains(controlKey)
    );
  }

  processFormValidation(inputForm: FormGroup, dialogFields: DialogField[], requirements: any): void {
    dialogFields.forEach((field: DialogField) => {
      const fieldValidators = this.buildFieldValidators(field, inputForm, requirements);
      if (!inputForm.controls[field.key]) {
        let value = DialogFieldFormatterHelper.getFormattedText(field, field.value);;
        const formControl = new FormControl(value, fieldValidators);
        inputForm.addControl(field.key, formControl);
      }
      else{
        this.processFieldRequirement(inputForm, field, requirements);
      }
    });
  }
  private processFieldRequirement(inputForm: FormGroup, dialogField: any, requirements: any) {
    const fieldValidators = this.buildFieldValidators(dialogField, inputForm, requirements);
    const fieldFormControl = inputForm.get(dialogField.key);
    const requiredValidator = ValidatorHelper.getValidatorFn(inputForm, 'Required');
    if(this.hasValidatorControl(fieldFormControl, requiredValidator) && !requirements[dialogField.key]){
      this.updateValueAndValidity(inputForm, dialogField, fieldValidators);
    }
    if(!this.hasValidatorControl(fieldFormControl, requiredValidator) && requirements[dialogField.key]){
      if(!dialogField.validator.includes('Required')){
        dialogField.validator.push('Required');
      }
      this.updateValueAndValidity(inputForm, dialogField, fieldValidators);
    }
  }

  private updateValueAndValidity(inputForm: FormGroup<any>, dialogField: any, fieldValidators: any[]) {
    inputForm.controls[dialogField.key].clearValidators();
    inputForm.controls[dialogField.key].setValidators(fieldValidators);
    inputForm.controls[dialogField.key].updateValueAndValidity();
  }

  private hasValidatorControl(control: AbstractControl, validator: any): boolean {
    if (!control.validator) return false; // No validators present
    const validatorFn = control.validator({} as AbstractControl);
    return validatorFn && validatorFn[validator.name];
  }
  private buildFieldValidators(field: DialogField, inputForm: FormGroup, requirements: any): any[] {
    const fieldValidators = [];

    // Process custom validators
    const validators = field.fieldValues['Validators'] as ICustomValidatorModel[];

    if (validators) {
      validators.forEach(validator => {
        if (ValidatorHelper.hasValidator(validator.key)) {
          return;
        }

        if (field.type === "Group" && validator.key === "RequiredEliteFormGroup") {
          ValidatorHelper.registerValidator(eliteFormGroupValidator);
        }

        if (validator.regex) {
          ValidatorHelper.registerValidator({
            key: validator.key,
            message: validator.message,
            validatorFn: (formControl: FormControl) => {
              if (!formControl.value) return null;

              const isValid = formControl.value.match(validator.regex);
              return isValid ? null : { [validator.errorKey]: isValid };
            },
            errorKey: validator.errorKey,
          });
        }
      });
    }

    // Process field validators
    if (field.validator) {
      field.validator.forEach(fieldValidator => {
        if (ValidatorHelper.hasValidator(fieldValidator)) {
          fieldValidators.push(ValidatorHelper.getValidatorFn(inputForm, fieldValidator));
        }
      });
    }

    if (requirements) {
      const requiredValidator = ValidatorHelper.getValidatorFn(inputForm, 'Required');
      if (this.hasValidator(fieldValidators, requiredValidator) && !requirements[field.key]) {
        fieldValidators.splice(fieldValidators.indexOf(requiredValidator), 1);
      }

      if (!this.hasValidator(fieldValidators, requiredValidator) && requirements[field.key]) {
        fieldValidators.push(requiredValidator);
      }
    }
    return fieldValidators;
  }

  validateAllForms() {
    Object.values(this.formGroups).forEach((formGroup) => {
      Object.values(formGroup.controls).forEach(control => {
        control.markAsTouched();
        control.markAsDirty();
        control.updateValueAndValidity();
      });
    });
  }

  private updateValidationState(): void {
    const groupErrors: { [key: string]: number } = {};
    let totalErrors = 0;

    Object.entries(this.formGroups).forEach(([groupId, formGroup]) => {
      const errors = Object.values(formGroup.controls)
        .filter(control => control.errors && (control.touched || control.dirty))
        .length;

      groupErrors[groupId] = errors;
      totalErrors += errors;
    });

    this.validationState.next({
      groupErrors,
      totalErrors
    });
  }


  private hasValidator(fieldValidators: any[], validator: any): boolean {
    return fieldValidators.some(o => o.name === validator.name);
  }
}
